import { UserRole } from '../types';

// Ruolo per sede: un utente lavora nelle sedi di appartenenza (`sedeIds`/`sedi`)
// e ne gestisce un sottoinsieme (`sediGestiteIds`). È RESPONSABILE se ne gestisce
// almeno una; il titolare è globale e non ha sedi gestite.

export const MSG_RESPONSABILE_SENZA_SEDE = 'Un responsabile deve gestire almeno una sede';
export const MSG_GESTITE_FUORI_APPARTENENZA = 'Le sedi gestite devono essere tra le sedi dell\'utente';

export const MSG_403_GENERICO = 'Non hai i permessi per eseguire questa operazione.';
export const MSG_403_RESPONSABILE =
    'Non hai i permessi per questa operazione. Se riguarda una sede che non gestisci, '
    + 'i tuoi incarichi e turni di quella sede sono in "Miei Incarichi".';

type UserLike = {
    role?: string | null;
    sedeId?: number | null;
    sedeIds?: number[] | null;
    sedi?: { id: number; name?: string }[] | null;
    sediGestiteIds?: number[] | null;
} | null | undefined;

const toIds = (arr: unknown): number[] =>
    Array.isArray(arr)
        ? arr.map((x: any) => Number(x?.id ?? x)).filter(n => Number.isFinite(n))
        : [];

/** Sedi di appartenenza (dove l'utente lavora). */
export const getSediAppartenenzaIds = (user: UserLike): number[] => {
    if (!user) return [];
    const fromSedi = toIds(user.sedi);
    if (fromSedi.length > 0) return fromSedi;
    const fromIds = toIds(user.sedeIds);
    if (fromIds.length > 0) return fromIds;
    return user.sedeId != null ? [Number(user.sedeId)] : [];
};

/**
 * Sedi gestite dal responsabile. Se il payload non porta ancora il campo
 * (sessione salvata prima dell'aggiornamento) valgono le sedi di appartenenza;
 * il backend resta comunque l'arbitro e risponde 403 sulle altre.
 */
export const getSediGestiteIds = (user: UserLike): number[] => {
    if (!user || user.role !== UserRole.RESPONSABILE) return [];
    if (Array.isArray(user.sediGestiteIds)) return toIds(user.sediGestiteIds);
    return getSediAppartenenzaIds(user);
};

export const isSedeGestita = (user: UserLike, sedeId: number | string | null | undefined): boolean =>
    sedeId != null && getSediGestiteIds(user).some(id => String(id) === String(sedeId));

/**
 * Sede di partenza del selettore per il responsabile: quella richiesta se la
 * gestisce, altrimenti la prima gestita. Null se non ne gestisce nessuna.
 */
export const resolveSedeGestitaIniziale = (
    user: UserLike,
    preferita?: number | string | null,
): number | null => {
    const gestite = getSediGestiteIds(user);
    if (preferita != null && preferita !== 'ALL') {
        const hit = gestite.find(id => String(id) === String(preferita));
        if (hit != null) return hit;
    }
    if (user?.sedeId != null && gestite.some(id => String(id) === String(user.sedeId))) {
        return Number(user.sedeId);
    }
    return gestite[0] ?? null;
};

/** Stessa validazione del server sul ruolo per sede. Null se valido. */
export const validateSediGestite = (
    role: UserRole | string,
    sedeIds: number[],
    sediGestiteIds: number[],
): string | null => {
    if (role !== UserRole.RESPONSABILE) return null;
    if (sediGestiteIds.length === 0) return MSG_RESPONSABILE_SENZA_SEDE;
    if (sediGestiteIds.some(id => !sedeIds.includes(id))) return MSG_GESTITE_FUORI_APPARTENENZA;
    return null;
};

/**
 * Sedi gestite da inviare al server: solo per il responsabile e solo tra quelle
 * spuntate, nell'ordine dell'elenco sedi.
 */
export const normalizeSediGestite = (
    role: UserRole | string,
    sedeIds: number[],
    sediGestiteIds: number[],
): number[] => role === UserRole.RESPONSABILE
    ? sedeIds.filter(id => sediGestiteIds.includes(id))
    : [];

export interface RiepilogoRuoli {
    responsabile: string[];
    collaboratore: string[];
}

export const buildRiepilogoRuoli = (
    role: UserRole | string,
    sedeIds: number[],
    sediGestiteIds: number[],
    nomeSede: (id: number) => string,
): RiepilogoRuoli => {
    const gestite = normalizeSediGestite(role, sedeIds, sediGestiteIds);
    return {
        responsabile: gestite.map(nomeSede),
        collaboratore: sedeIds.filter(id => !gestite.includes(id)).map(nomeSede),
    };
};

/** "Sarà responsabile di: A, B · collaboratore di: C" */
export const formatRiepilogoRuoli = (r: RiepilogoRuoli): string => {
    const lista = (nomi: string[]) => (nomi.length > 0 ? nomi.join(', ') : 'nessuna sede');
    return `Sarà responsabile di: ${lista(r.responsabile)} · collaboratore di: ${lista(r.collaboratore)}`;
};

const sameSet = (a: number[], b: number[]) =>
    a.length === b.length && a.every(x => b.includes(x));

/**
 * True se il salvataggio cambia ruolo o sedi gestite: in quel caso serve la
 * conferma con il riepilogo.
 */
export const isCambioRuoloOSediGestite = (
    prima: { role: string; sediGestiteIds: number[] },
    dopo: { role: string; sediGestiteIds: number[] },
): boolean => prima.role !== dopo.role || !sameSet(prima.sediGestiteIds, dopo.sediGestiteIds);

export const messaggioPermessoNegato = (role?: string | null): string =>
    role === UserRole.RESPONSABILE ? MSG_403_RESPONSABILE : MSG_403_GENERICO;
